import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { confirmEmployeeLogout } from "../../auth/employeeLogout";
import { EmptyState } from "../../components/EmptyState";
import { Footer } from "../../components/Footer";
import { NotificationCenter } from "../../components/NotificationCenter";
import { Skeleton } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { ApiError, apiGet, apiSend } from "../../lib/customFetch";
import { AbsenceRequests } from "./AbsenceRequests";
import { useFeatures } from "../../lib/useFeatures";
import { useMe } from "../../lib/useMe";
import { formatMinutter, formatVarighed, klokken } from "./format";
import {
  HistoryCorrections,
  type HistoryCorrection,
} from "./HistoryCorrections";
import { StandardStemplePage } from "./StandardStemplePage";
import {
  attentionStamps,
  dismissQuarantinedStamp,
  enqueueStamp,
  flushStampQueue,
  initStampSync,
  MAX_AUTO_RETRY_ATTEMPTS,
  pendingStamps,
  quarantinedStamps,
  replaceAttentionStamp,
  retryAttentionStamp,
  selectStampQueueScope,
  type FlushResult,
  type FlushRetryStatus,
  type StampSyncController,
} from "./queue";
import styles from "./StemplePage.module.css";

interface Entry {
  id: string;
  clockInAt: string;
  clockOutAt: string | null;
  note: string | null;
  projektId?: string | null;
  projektNavn?: string | null;
  projektKundenavn?: string | null;
}

interface Projekt {
  id: string;
  navn: string;
  kundenavn: string | null;
  aktiv: boolean;
}

interface StatusData {
  aaben: Entry | null;
  iDagMinutter: number;
  pauseMinutter: number;
  pauseKilde: "auto" | "manuel" | "ingen";
  nettoMinutter: number;
  // M-11 (additivt): kun med når projekt-featuren er slået til for tenanten.
  projekterAktiv?: boolean;
  projekter?: Projekt[];
  aabentProjekt?: Projekt | null;
}

/** Husk medarbejderens senest valgte projekt mellem stemplinger (pr. enhed). */
const SIDSTE_PROJEKT_KEY = "appsmeden_sidste_projekt";

const cphDato = new Intl.DateTimeFormat("sv-SE", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  timeZone: "Europe/Copenhagen",
});

const cphDatoTekst = new Intl.DateTimeFormat("da-DK", {
  day: "numeric",
  month: "short",
  timeZone: "Europe/Copenhagen",
});

export function aabenSidenTekst(clockInAt: string, nu: Date): string {
  const start = new Date(clockInAt);
  return cphDato.format(start) === cphDato.format(nu)
    ? `inde siden kl. ${klokken.format(start)}`
    : `inde siden ${cphDatoTekst.format(start)} kl. ${klokken.format(start)}`;
}

interface WeekData {
  totalMinutter: number;
  totalPauseMinutter: number;
  totalNettoMinutter: number;
  stemplinger: Entry[];
}

type PageState =
  | { status: "loading" }
  | { status: "ok"; data: StatusData; uge: WeekData }
  | { status: "feature_off" }
  | { status: "no_auth" }
  | { status: "failed" };

/** Ét levende sekund-tick driver både uret og varigheden (D.1). */
function useNu(): number {
  const [nu, setNu] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNu(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return nu;
}

/** Haptisk kvittering hvor enheden kan (feature-detected, aldrig krævet). */
function vibrer(): void {
  if ("vibrate" in navigator) navigator.vibrate(35);
}

function tilLokaltInput(iso: string): string {
  const date = new Date(iso);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function retryVentetid(nextAttemptAt: string, nu: number): string {
  const sekunder = Math.max(
    0,
    Math.ceil((new Date(nextAttemptAt).getTime() - nu) / 1000),
  );
  return sekunder < 60
    ? `${String(sekunder)} sek.`
    : `${String(Math.ceil(sekunder / 60))} min.`;
}

/**
 * Medarbejderens stemple-skærm — fuldskærm, mobile-first, én stor knap.
 * Optimistic UI: tilstanden skifter øjeblikkeligt; requesten går gennem
 * offline-køen (kø-først-design), så offline og online er samme kodepath.
 * Permanente 4xx-fejl bevares lokalt som "kræver hjælp"; transiente fejl
 * bliver i køen til automatisk retry.
 */
export function StemplePage() {
  const toast = useToast();
  const { features } = useFeatures();
  const { me, loading: meLoading, error: meError } = useMe();
  const [, navigate] = useLocation();
  const [state, setState] = useState<PageState>({ status: "loading" });
  const [note, setNote] = useState("");
  const [valgtProjekt, setValgtProjekt] = useState("");
  const [pending, setPending] = useState(0);
  const [attention, setAttention] = useState<ReturnType<typeof attentionStamps>>(
    [],
  );
  const [quarantine, setQuarantine] = useState<
    ReturnType<typeof quarantinedStamps>
  >([]);
  const [retterProblem, setRetterProblem] = useState(false);
  const [rettetTid, setRettetTid] = useState("");
  const [rettetNote, setRettetNote] = useState("");
  const [lokalLagerFejl, setLokalLagerFejl] = useState<string | null>(null);
  const [kvittering, setKvittering] = useState<"ind" | "ud" | null>(null);
  const [senderAntal, setSenderAntal] = useState(0);
  const [retry, setRetry] = useState<FlushRetryStatus | null>(null);
  const [serverBekraeftet, setServerBekraeftet] = useState(0);
  const [historikVersion, setHistorikVersion] = useState(0);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const nu = useNu();
  const stateRef = useRef(state);
  stateRef.current = state;
  const kvitteringTimer = useRef<number | undefined>(undefined);
  const serverTimer = useRef<number | undefined>(undefined);
  const syncRef = useRef<StampSyncController | null>(null);

  const resync = useCallback(() => {
    Promise.all([
      apiGet<StatusData>("/api/clockin/status"),
      apiGet<WeekData>("/api/clockin/week"),
    ])
      .then(([data, uge]) => setState({ status: "ok", data, uge }))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: "feature_off" });
        } else if (err instanceof ApiError && err.status === 401) {
          setState({ status: "no_auth" });
        } else if (stateRef.current.status !== "ok") {
          // Offline med kendt tilstand: behold den optimistiske visning.
          setState({ status: "failed" });
        }
      });
  }, []);

  const efterFlush = useCallback(
    (result: FlushResult) => {
      setLokalLagerFejl(null);
      setPending(result.remaining);
      setRetry(result.retry ?? null);
      setAttention(attentionStamps());
      setQuarantine(quarantinedStamps());
      if (result.needsAttention > 0) {
        toast.show(
          `${String(result.needsAttention)} stempling${result.needsAttention === 1 ? "" : "er"} kræver hjælp: ${result.attentionMessage ?? "kontakt din leder."}`,
          "error",
        );
      }
      if (result.sent > 0 || result.needsAttention > 0) {
        resync();
      }
      if (result.sent > 0) {
        // Historikken må først genhentes, når serveren faktisk har bekræftet
        // mutationen. Et optimistisk status-skift kan ellers hente den gamle
        // åbne stempling i kapløb med POST-kaldet.
        setHistorikVersion((version) => version + 1);
      }
      if (result.sent > 0 && result.remaining === 0) {
        setServerBekraeftet(result.sent);
        window.clearTimeout(serverTimer.current);
        serverTimer.current = window.setTimeout(
          () => setServerBekraeftet(0),
          4000,
        );
      }
    },
    [resync, toast],
  );

  const genindlaesLokalKoe = useCallback(() => {
    setPending(pendingStamps().length);
    setAttention(attentionStamps());
    setQuarantine(quarantinedStamps());
  }, []);

  const haandterFlushFejl = useCallback(() => {
    genindlaesLokalKoe();
    setLokalLagerFejl(
      "Stemplingerne kunne ikke flyttes sikkert i enhedens lokale lager. De er bevaret — frigør plads og prøv igen.",
    );
  }, [genindlaesLokalKoe]);

  const flushKoe = useCallback((resetRetry = false) => {
    setServerBekraeftet(0);
    const sync = syncRef.current;
    if (sync !== null) {
      sync.flushNow({ resetRetry });
      return;
    }
    setSenderAntal(pendingStamps().length);
    void flushStampQueue()
      .then(efterFlush)
      .catch(haandterFlushFejl)
      .finally(() => setSenderAntal(0));
  }, [efterFlush, haandterFlushFejl]);

  const proevIgen = () => {
    const problem = attention[0];
    if (problem === undefined || !retryAttentionStamp(problem.clientId)) return;
    setRetterProblem(false);
    genindlaesLokalKoe();
    flushKoe(true);
  };

  const aabnRettelse = () => {
    const problem = attention[0];
    if (problem === undefined) return;
    setRettetTid(tilLokaltInput(problem.timestamp));
    setRettetNote(problem.note ?? "");
    setRetterProblem(true);
  };

  const gemOgProevIgen = () => {
    const problem = attention[0];
    const tidspunkt = new Date(rettetTid);
    if (problem === undefined || Number.isNaN(tidspunkt.getTime())) {
      toast.show("Vælg et gyldigt tidspunkt.", "error");
      return;
    }
    const replaced = replaceAttentionStamp(problem.clientId, {
      timestamp: tidspunkt.toISOString(),
      ...(problem.type === "out" ? { note: rettetNote.trim() } : {}),
    });
    if (!replaced) {
      toast.show("Rettelsen kunne ikke gemmes på enheden.", "error");
      return;
    }
    setRetterProblem(false);
    genindlaesLokalKoe();
    flushKoe(true);
  };

  useEffect(() => {
    if (meLoading) return;
    if (me === null) {
      setState({ status: meError === "unauthorized" ? "no_auth" : "failed" });
      return;
    }
    if (me.clockinScope === undefined) {
      setState({ status: "failed" });
      return;
    }

    selectStampQueueScope(me.clockinScope);
    const projektKey = `${SIDSTE_PROJEKT_KEY}:${me.clockinScope}`;
    localStorage.removeItem(SIDSTE_PROJEKT_KEY);
    setValgtProjekt(localStorage.getItem(projektKey) ?? "");
    genindlaesLokalKoe();
    resync();
    const sync = initStampSync(
      efterFlush,
      haandterFlushFejl,
      setSenderAntal,
    );
    syncRef.current = sync;
    return () => {
      syncRef.current = null;
      sync.dispose();
    };
  }, [
    me,
    meError,
    meLoading,
    resync,
    efterFlush,
    haandterFlushFejl,
    genindlaesLokalKoe,
  ]);

  useEffect(() => {
    const blevOnline = () => setOnline(true);
    const blevOffline = () => setOnline(false);
    window.addEventListener("online", blevOnline);
    window.addEventListener("offline", blevOffline);
    return () => {
      window.removeEventListener("online", blevOnline);
      window.removeEventListener("offline", blevOffline);
    };
  }, []);

  useEffect(
    () => () => {
      window.clearTimeout(kvitteringTimer.current);
      window.clearTimeout(serverTimer.current);
    },
    [],
  );

  const stemple = () => {
    if (state.status !== "ok") return;
    const erInde = state.data.aaben !== null;
    const tidspunkt = new Date();
    // M-11: projekt vælges KUN ved indstempling og kun når featuren er til.
    const projekterAktiv = state.data.projekterAktiv === true;
    const projektValg =
      !erInde && projekterAktiv && valgtProjekt.length > 0
        ? valgtProjekt
        : undefined;
    const projekt = state.data.projekter?.find(
      (item) => item.id === projektValg,
    );

    // Taktil + visuel kvittering med det samme — nettet kommer bagefter.
    vibrer();
    setKvittering(erInde ? "ud" : "ind");
    window.clearTimeout(kvitteringTimer.current);
    kvitteringTimer.current = window.setTimeout(
      () => setKvittering(null),
      1600,
    );

    // Optimistic: skift tilstanden ØJEBLIKKELIGT.
    setState({
      ...state,
      data: erInde
        ? { ...state.data, aaben: null, aabentProjekt: null }
        : {
            ...state.data,
            aaben: {
              id: "optimistisk",
              clockInAt: tidspunkt.toISOString(),
              clockOutAt: null,
              note: null,
              ...(projektValg === undefined ? {} : { projektId: projektValg }),
            },
            aabentProjekt: projekt ?? null,
          },
    });
    enqueueStamp({
      clientId: crypto.randomUUID(),
      type: erInde ? "out" : "in",
      timestamp: tidspunkt.toISOString(),
      ...(erInde && note.trim().length > 0 ? { note: note.trim() } : {}),
      ...(projektValg !== undefined ? { projektId: projektValg } : {}),
    });
    if (projektValg !== undefined) {
      if (me?.clockinScope !== undefined) {
        localStorage.setItem(
          `${SIDSTE_PROJEKT_KEY}:${me.clockinScope}`,
          projektValg,
        );
      }
    }
    setNote("");
    setPending(pendingStamps().length);
    flushKoe(true);
  };

  // M-10: funktionærer med standarddag-mode får en helt anden flade — de
  // stempler ikke, men ser deres ugeskema og registrerer kun afvigelser.
  if (me?.registreringsMode === "standard") {
    return (
      <StandardStemplePage
        navn={me.navn}
        firma={me.tenantNavn}
        koerselAktiv={features.some((feature) => feature.key === "koersel")}
        fravaerTilladt={me.rolle === "medarbejder"}
        enabledFeatureKeys={features.map((feature) => feature.key)}
      />
    );
  }

  if (state.status === "loading") {
    return (
      <div className={styles.page}>
        <div className={styles.stempleOmraade}>
          <Skeleton width="12rem" height="4rem" />
          <div className={styles.knapSkelet} aria-hidden="true" />
          <Skeleton width="8rem" height="1rem" />
        </div>
      </div>
    );
  }

  if (state.status === "no_auth") {
    return (
      <div className={styles.page}>
        <EmptyState
          title="Enheden er ikke koblet på"
          description="Åbn invitationslinket fra din arbejdsgiver igen, eller bed om et nyt."
        />
      </div>
    );
  }

  if (state.status === "feature_off") {
    return (
      <div className={styles.page}>
        <EmptyState
          title="Tidsregistrering er ikke slået til"
          description="Din virksomhed har ikke tidsregistrering aktiveret."
        />
      </div>
    );
  }

  if (state.status === "failed") {
    return (
      <div className={styles.page}>
        <EmptyState
          title="Kunne ikke hente din status"
          description="Tjek forbindelsen — dine stemplinger gemmes lokalt imens."
        />
      </div>
    );
  }

  const { aaben, iDagMinutter } = state.data;
  const erInde = aaben !== null;
  const nuDato = new Date(nu);
  // M-11: projekt-dropdown vises kun når featuren er til og der findes projekter.
  const projekterAktiv = state.data.projekterAktiv === true;
  const projekter = state.data.projekter ?? [];
  const visProjektValg = !erInde && projekterAktiv && projekter.length > 0;
  // Fald tilbage til "uden projekt" hvis det huskede projekt er deaktiveret.
  const projektValue = projekter.some((p) => p.id === valgtProjekt)
    ? valgtProjekt
    : "";
  const aktivtProjekt = erInde
    ? (state.data.aabentProjekt ??
      projekter.find((projekt) => projekt.id === aaben.projektId) ??
      null)
    : null;

  return (
    <div className={`${styles.page} ${erInde ? styles.pageInde ?? "" : ""}`}>
      <a className={styles.skipLink} href="#main-content">
        Spring til hovedindhold
      </a>
      <header className={styles.top}>
        <div className={styles.brand}>
          app<span>smeden</span>
        </div>
        {me !== null && (
          <div className={styles.hilsen}>
            <p className={styles.navn}>{me.navn}</p>
            <p className={styles.firma}>{me.tenantNavn}</p>
          </div>
        )}
        <nav className={styles.kontoNav} aria-label="Konto">
          <NotificationCenter
            enabledFeatureKeys={features.map((feature) => feature.key)}
          />
          {features.some((feature) => feature.key === "koersel") && (
            <Link href="/koersel">Kørsel</Link>
          )}
          <Link href="/stemple/enheder">Enheder</Link>
          <button
            type="button"
            onClick={() => {
              if (!confirmEmployeeLogout()) return;
              void apiSend("POST", "/api/logout").finally(() =>
                navigate("/login"),
              );
            }}
          >
            Log ud
          </button>
        </nav>
      </header>

      <main id="main-content" className={styles.main} tabIndex={-1}>
      <h1 className={styles.srOnly}>Min tidsregistrering</h1>
      <div className={styles.stempleOmraade}>
        {/* Hero: levende ur når man er ude; levende varighed når man er inde. */}
        {erInde ? (
          <div className={styles.hero}>
            <p className={styles.heroTal} aria-label="Tid siden indstempling">
              {formatVarighed(nu - new Date(aaben.clockInAt).getTime())}
            </p>
            <p className={styles.heroUnder}>
              <strong>{aabenSidenTekst(aaben.clockInAt, nuDato)}</strong>
            </p>
            {aktivtProjekt !== null && (
              <p className={styles.aktivtProjekt} aria-label="Aktivt projekt">
                <span>Projekt</span>
                <strong>
                  {aktivtProjekt.kundenavn === null
                    ? aktivtProjekt.navn
                    : `${aktivtProjekt.navn} · ${aktivtProjekt.kundenavn}`}
                </strong>
              </p>
            )}
          </div>
        ) : (
          <div className={styles.hero}>
            <p className={styles.heroTal} aria-label="Klokken nu">
              {klokken.format(nuDato)}
            </p>
            <p className={styles.heroUnder}>Du er ikke stemplet ind</p>
          </div>
        )}

        {visProjektValg && (
          <label className={styles.projektVaelger}>
            <span className={styles.projektLabel}>Projekt (valgfrit)</span>
            <select
              className={styles.projektSelect}
              value={projektValue}
              onChange={(e) => setValgtProjekt(e.target.value)}
            >
              <option value="">Uden projekt</option>
              {projekter.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.kundenavn !== null ? `${p.navn} · ${p.kundenavn}` : p.navn}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className={styles.knapOmraade}>
          <button
            type="button"
            className={[
              styles.stempleKnap,
              erInde ? styles.stempleKnapUd : null,
              kvittering !== null ? styles.stempleKnapKvitteret : null,
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={stemple}
          >
            {erInde ? "Stemple ud" : "Stemple ind"}
          </button>
          {kvittering !== null && (
            <p className={styles.kvittering} role="status">
              {kvittering === "ind" ? "Stemplet ind" : "Stemplet ud"} ✓
            </p>
          )}
        </div>

        {erInde && (
          <textarea
            className={styles.noteFelt}
            rows={2}
            maxLength={500}
            placeholder="Note til udstempling (valgfri)"
            aria-label="Note til udstempling"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}

        {lokalLagerFejl !== null && (
          <div className={styles.kraeverHjaelp} role="alert">
            <strong>Enhedens lokale lager er fuldt</strong>
            <span>{lokalLagerFejl}</span>
            <div className={styles.problemHandlinger}>
              <button type="button" onClick={() => flushKoe(true)}>
                Prøv igen
              </button>
            </div>
          </div>
        )}
        {pending > 0 && (
          <div className={styles.venter} role="status">
            <span className={styles.venterPrik} aria-hidden="true" />
            {senderAntal > 0 ? (
              <>Gemmer {senderAntal} stempling{senderAntal === 1 ? "" : "er"}…</>
            ) : !online ? (
              <>Gemt på denne enhed — sender når forbindelsen er tilbage.</>
            ) : retry?.paused === true ? (
              <>
                Gemt på denne enhed, men kunne ikke synkroniseres efter {MAX_AUTO_RETRY_ATTEMPTS} forsøg.
                <button type="button" onClick={() => flushKoe(true)}>
                  Prøv igen
                </button>
              </>
            ) : retry?.nextAttemptAt !== undefined ? (
              <>
                Gemt på denne enhed · afventer forbindelse · forsøg {retry.attempts} af {retry.maxAttempts}
                {" "}— prøver igen om {retryVentetid(retry.nextAttemptAt, nu)}
              </>
            ) : (
              <>{pending} stempling{pending === 1 ? "" : "er"} er gemt på denne enhed og venter på at blive synkroniseret.</>
            )}
          </div>
        )}
        {pending === 0 && serverBekraeftet > 0 && (
          <div className={styles.venter} role="status">
            <span className={styles.venterPrik} aria-hidden="true" />
            {serverBekraeftet} stempling{serverBekraeftet === 1 ? "" : "er"} er gemt.
          </div>
        )}
        {attention.length > 0 && (
          <div className={styles.kraeverHjaelp} role="alert">
            <strong>
              {attention.length} stempling{attention.length === 1 ? "" : "er"}
              {" "}kræver hjælp
            </strong>
            <span>
              {new Date(attention[0]!.timestamp).toLocaleString("da-DK")}: {" "}
              {attention[0]!.error} Kontakt din leder, så tiden kan afklares.
            </span>
            <div className={styles.problemHandlinger}>
              <button type="button" onClick={proevIgen}>
                Prøv igen
              </button>
              <button type="button" onClick={aabnRettelse}>
                Ret tidspunkt
              </button>
            </div>
            {retterProblem && (
              <div className={styles.problemFormular}>
                <label>
                  Tidspunkt
                  <input
                    type="datetime-local"
                    value={rettetTid}
                    onChange={(event) => setRettetTid(event.target.value)}
                  />
                </label>
                {attention[0]!.type === "out" && (
                  <label>
                    Note
                    <textarea
                      rows={2}
                      maxLength={500}
                      value={rettetNote}
                      onChange={(event) => setRettetNote(event.target.value)}
                    />
                  </label>
                )}
                <div className={styles.problemHandlinger}>
                  <button type="button" onClick={gemOgProevIgen}>
                    Gem og prøv igen
                  </button>
                  <button type="button" onClick={() => setRetterProblem(false)}>
                    Annuller
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {quarantine.length > 0 && (
          <div className={styles.karantæne} role="alert">
            <strong>En lokal stempling kunne ikke læses</strong>
            <span>
              Den er gemt sikkert på enheden og bliver ikke sendt. Kontakt din
              leder, hvis tiden mangler.
            </span>
            <button
              type="button"
              onClick={() => {
                if (
                  !window.confirm(
                    "Slet den ulæselige lokale kopi? Handlingen kan ikke fortrydes.",
                  )
                ) {
                  return;
                }
                dismissQuarantinedStamp(quarantine[0]!.id);
                genindlaesLokalKoe();
              }}
            >
              Slet lokal kopi
            </button>
          </div>
        )}
      </div>

      <div className={styles.statsRaekke}>
        <div className={styles.stat}>
          <p className={styles.statVaerdi}>{formatMinutter(iDagMinutter)}</p>
          <p className={styles.statLabel}>I dag (afsluttet)</p>
          {state.data.pauseMinutter > 0 && (
            <p className={styles.statPause}>
              − {formatMinutter(state.data.pauseMinutter)} pause ={" "}
              {formatMinutter(state.data.nettoMinutter)}
            </p>
          )}
        </div>
        <div className={styles.stat}>
          <p className={styles.statVaerdi}>
            {formatMinutter(state.uge.totalMinutter)}
          </p>
          <p className={styles.statLabel}>Denne uge</p>
          {state.uge.totalPauseMinutter > 0 && (
            <p className={styles.statPause}>
              − {formatMinutter(state.uge.totalPauseMinutter)} pause ={" "}
              {formatMinutter(state.uge.totalNettoMinutter)}
            </p>
          )}
        </div>
      </div>

      {me?.rolle === "medarbejder" && (
        <AbsenceRequests variant={erInde ? "dark" : "light"} />
      )}

      <HistorikSektion
        aktivStempling={erInde}
        refreshKey={String(historikVersion)}
        variant={erInde ? "moerk" : "lys"}
      />
      </main>

      <Footer variant={erInde ? "moerk" : "lys"} />
    </div>
  );
}

interface HistorikDag {
  dato: string;
  bruttoMinutter: number;
  pauseMinutter: number;
  pauseKilde: "auto" | "manuel" | "ingen";
  nettoMinutter: number;
  aaben: boolean;
  stemplinger: Entry[];
  fravaerType?: "ferie" | "sygdom" | "fri";
  fravaerNote?: string | null;
}

interface HistorikData {
  ugeStart: string;
  dage: HistorikDag[];
  totalBruttoMinutter: number;
  totalPauseMinutter: number;
  totalNettoMinutter: number;
  /** Ældste viselige uge (2 mdr. bagud) — ældre data via chefens eksport. */
  aeldsteUge: string;
  /** Fase 4b: ugen er godkendt af chefen og låst. */
  godkendt: boolean;
  rettelser: HistoryCorrection[];
  naesteRettelsesCursor: string | null;
}

const historikDagFmt = new Intl.DateTimeFormat("da-DK", {
  weekday: "long",
  day: "numeric",
  month: "short",
});

function flytUge(ugeStart: string, uger: number): string {
  const d = new Date(`${ugeStart}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + uger * 7);
  return d.toISOString().slice(0, 10);
}

/**
 * B-014 (fase 2b): medarbejderens egen dag/uge-historik inkl. pauser og
 * status — lovkravets adgang til egne registreringer. Mobile-first liste.
 */
function HistorikSektion({
  aktivStempling,
  refreshKey,
  variant,
}: {
  aktivStempling: boolean;
  refreshKey: string;
  variant: "lys" | "moerk";
}) {
  const [uge, setUge] = useState<string | null>(null);
  const [data, setData] = useState<HistorikData | null>(null);
  const [fejl, setFejl] = useState(false);
  const [henterFlere, setHenterFlere] = useState(false);
  const [sideFejl, setSideFejl] = useState(false);

  const hent = useCallback(() => {
    const query = uge === null ? "" : `?uge=${uge}`;
    apiGet<HistorikData>(`/api/clockin/history${query}`)
      .then((d) => {
        setData(d);
        setFejl(false);
        setSideFejl(false);
      })
      .catch(() => setFejl(true));
  }, [uge, refreshKey]);

  useEffect(() => {
    hent();
  }, [hent]);

  if (fejl) {
    return (
      <section className={styles.historik} aria-label="Din historik">
        <p className={styles.tomUge} role="alert">
          Kunne ikke hente din historik — tjek forbindelsen.
        </p>
        <button
          type="button"
          className={styles.historikNavLabel}
          onClick={hent}
        >
          Prøv igen
        </button>
      </section>
    );
  }
  if (data === null) return null;

  const erIndevaerende = uge === null;
  const dageMedIndhold = data.dage.filter(
    (d) => d.stemplinger.length > 0 || d.fravaerType !== undefined,
  );
  const hentFlere = () => {
    if (data.naesteRettelsesCursor === null || henterFlere) return;
    const params = new URLSearchParams({
      limit: "25",
      cursor: data.naesteRettelsesCursor,
    });
    if (uge !== null) params.set("uge", uge);
    setHenterFlere(true);
    setSideFejl(false);
    apiGet<HistorikData>(`/api/clockin/history?${params.toString()}`)
      .then((next) => {
        setData((current) =>
          current === null
            ? next
            : {
                ...next,
                rettelser: [...current.rettelser, ...next.rettelser],
              },
        );
      })
      .catch(() => setSideFejl(true))
      .finally(() => setHenterFlere(false));
  };

  return (
    <section className={styles.historik} aria-label="Din historik">
      <div className={styles.historikHeader}>
        <h2 className={styles.historikTitel}>Din historik</h2>
        <div className={styles.historikNav}>
          <button
            type="button"
            className={styles.historikNavKnap}
            aria-label="Forrige uge"
            disabled={data.ugeStart <= data.aeldsteUge}
            onClick={() => setUge(flytUge(data.ugeStart, -1))}
          >
            ‹
          </button>
          <button
            type="button"
            className={styles.historikNavLabel}
            onClick={() => setUge(null)}
            disabled={erIndevaerende}
          >
            {erIndevaerende ? "Denne uge" : `Uge fra ${new Intl.DateTimeFormat("da-DK", { day: "numeric", month: "short" }).format(new Date(`${data.ugeStart}T00:00:00`))}`}
          </button>
          <button
            type="button"
            className={styles.historikNavKnap}
            aria-label="Næste uge"
            onClick={() => setUge(flytUge(data.ugeStart, 1))}
            disabled={erIndevaerende}
          >
            ›
          </button>
        </div>
      </div>

      {dageMedIndhold.length === 0 ? (
        <p className={styles.tomUge}>
          {erIndevaerende
            ? aktivStempling
              ? "Du er stemplet ind. Registreringen vises her, når du stempler ud."
              : "Ingen stemplinger endnu i denne uge — tryk på knappen, når du møder ind, så klarer den resten."
            : "Ingen registreringer i denne uge."}
        </p>
      ) : (
        <>
          <ul className={styles.historikListe}>
            {dageMedIndhold.map((d) => (
              <li key={d.dato} className={styles.historikDag}>
                <div className={styles.historikDagTop}>
                  <span className={styles.historikDato}>
                    {historikDagFmt.format(new Date(`${d.dato}T00:00:00`))}
                  </span>
                  <span className={styles.historikNetto}>
                    {d.aaben && (
                      <span className={styles.historikAaben}>åben</span>
                    )}
                    {formatMinutter(d.nettoMinutter)}
                  </span>
                </div>
                {d.fravaerType === undefined ? (
                  <ul className={styles.historikStemplinger}>
                    {d.stemplinger.map((s) => {
                      const projekt =
                        s.projektNavn == null
                          ? null
                          : s.projektKundenavn == null
                            ? s.projektNavn
                            : `${s.projektNavn} · ${s.projektKundenavn}`;
                      const detaljer = [projekt, s.note].filter(
                        (tekst): tekst is string => tekst !== null,
                      );
                      return (
                        <li key={s.id} className={styles.historikStempling}>
                          <span className={styles.historikTider}>
                            {klokken.format(new Date(s.clockInAt))}–
                            {s.clockOutAt === null
                              ? "nu"
                              : klokken.format(new Date(s.clockOutAt))}
                          </span>
                          {detaljer.length > 0 && (
                            <span className={styles.historikDetalje}>
                              {detaljer.join(" · ")}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className={styles.historikTider}>
                    {{ ferie: "Ferie", sygdom: "Sygdom", fri: "Fri" }[
                      d.fravaerType
                    ]}
                    {d.fravaerNote ? ` · ${d.fravaerNote}` : ""}
                  </p>
                )}
                {d.pauseMinutter > 0 && (
                  <p className={styles.historikPause}>
                    {formatMinutter(d.bruttoMinutter)} −{" "}
                    {formatMinutter(d.pauseMinutter)} pause (
                    {d.pauseKilde === "manuel" ? "rettet" : "auto"}) ={" "}
                    {formatMinutter(d.nettoMinutter)}
                  </p>
                )}
              </li>
            ))}
          </ul>
          <p className={styles.historikTotal}>
            Uge i alt: <strong>{formatMinutter(data.totalNettoMinutter)}</strong>
            {data.totalPauseMinutter > 0 &&
              ` (${formatMinutter(data.totalBruttoMinutter)} − ${formatMinutter(data.totalPauseMinutter)} pause)`}
            {data.godkendt && (
              <span className={styles.historikGodkendt}>✓ Godkendt af chefen</span>
            )}
          </p>
        </>
      )}
      {data.ugeStart <= data.aeldsteUge && (
        // Diskret linje (fase 3b): ældre data findes altid i eksporten.
        <p className={styles.historikGraense}>
          Her vises 2 måneder bagud — ældre uger kan hentes som eksport.
        </p>
      )}
      <HistoryCorrections
        rettelser={data.rettelser}
        naesteCursor={data.naesteRettelsesCursor}
        henterFlere={henterFlere}
        sideFejl={sideFejl}
        onHentFlere={hentFlere}
        variant={variant}
      />
    </section>
  );
}
